import { DiskID, DiskType, NodeID, Storage_Locality, StoragePolicyFailureDomain } from '@schema';
import type {
  ComputeProvisioningStoragePolicies,
  StoragePolicyIntent,
  StorageProvisioningDiskInventoryItem,
  StorageProvisioningStorageDiskIntent,
  StorageProvisioningNodeIntent
} from '@schema';
import { STORAGE_DISK_TYPE_TEXT } from '@/common/constants/storageDisks.constants';
import computedLocale from '@/common/utils/computedLocale';
import i18n from '@/common/i18n';

type Disk = StorageProvisioningNodeIntent['disks'][number];

export type GetInventoryDisk = (
  nodeId: NodeID,
  diskId: DiskID
) => StorageProvisioningDiskInventoryItem | undefined;

export type ComputeClusterStorageTier = {
  medium: DiskType;
  locality: Storage_Locality;
  count: number;
};

// Disk types from fastest to slowest.
export const MEDIUM_ORDER: DiskType[] = [DiskType.NVME, DiskType.SSD, DiskType.HDD];

export const sortMedia = (media: Iterable<DiskType>): DiskType[] => [...new Set(media)]
  .sort((a, b) => MEDIUM_ORDER.indexOf(a) - MEDIUM_ORDER.indexOf(b));

export const isMixedMedia = (media: DiskType[]): boolean => media.length > 1;

export const formatMedia = (media: DiskType[]): string => (media.length
  ? media.map(medium => STORAGE_DISK_TYPE_TEXT[medium] || medium).join(', ')
  : i18n.t('figure dash'));

export const STORAGE_LOCALITY_TEXT = computedLocale<Record<Storage_Locality, string>>(() => ({
  [Storage_Locality.LOCAL]: i18n.t('Local'),
  [Storage_Locality.SHARED]: i18n.t('Shared')
}));

const mergeStorageTiers = (tiers: ComputeClusterStorageTier[]): ComputeClusterStorageTier[] => {
  const merged = tiers.reduce<Record<string, ComputeClusterStorageTier>>((acc, tier) => {
    const key = `${tier.medium}:${tier.locality}`;
    return {
      ...acc,
      [key]: {
        medium: tier.medium,
        locality: tier.locality,
        count: (acc[key]?.count || 0) + tier.count
      }
    };
  }, {});
  return Object.values(merged);
};

// Disks of the same medium and locality form a single tier on the node.
export const getStorageTiers = (
  nodeId: NodeID,
  disks: Disk[],
  getInventoryDisk: GetInventoryDisk
): ComputeClusterStorageTier[] => mergeStorageTiers(
  disks.reduce<ComputeClusterStorageTier[]>((acc, disk) => {
    if (disk.role !== 'storage') {
      return acc;
    }
    const medium = getInventoryDisk(nodeId, disk.id)?.medium;
    if (!medium) {
      return acc;
    }
    return [
      ...acc,
      {
        medium,
        locality: disk.locality,
        count: 1
      }
    ];
  }, [])
);

export const formatStorageTier = ({ medium, locality, count }: ComputeClusterStorageTier): string => i18n.tc(
  '{count} {medium} disk ({locality})|{count} {medium} disks ({locality})',
  count,
  {
    medium: STORAGE_DISK_TYPE_TEXT[medium] || i18n.t('figure dash'),
    locality: STORAGE_LOCALITY_TEXT[locality] || i18n.t('figure dash'),
    count
  }
);

export const formatStorageTiers = (tiers: ComputeClusterStorageTier[]): string => tiers
  .map(formatStorageTier)
  .join(', ');

export type ComputeClusterDisksByMedium = {
  medium: DiskType;
  count: number;
};

// Both roles put metadata on the disk; mds-journal just adds a journal to it.
const isMetadataDisk = (disk: Disk): boolean => disk.role === 'mds' || disk.role === 'mds-journal';

// A journal without a tier is shared by every compatible tier on the node.
const isCommonJournalDisk = (disk: Disk): boolean => disk.role === 'journal' && disk.tier_num === null;

const getDisksByMedium = (
  nodeId: NodeID,
  disks: Disk[],
  getInventoryDisk: GetInventoryDisk,
  matches: (disk: Disk) => boolean
): ComputeClusterDisksByMedium[] => {
  const merged = disks.reduce<Record<string, ComputeClusterDisksByMedium>>((acc, disk) => {
    const medium = matches(disk) ? getInventoryDisk(nodeId, disk.id)?.medium : undefined;
    if (!medium) {
      return acc;
    }
    return {
      ...acc,
      [medium]: {
        medium,
        count: (acc[medium]?.count || 0) + 1
      }
    };
  }, {});
  return Object.values(merged);
};

export const getMetadataDisks = (
  nodeId: NodeID,
  disks: Disk[],
  getInventoryDisk: GetInventoryDisk
): ComputeClusterDisksByMedium[] => getDisksByMedium(nodeId, disks, getInventoryDisk, isMetadataDisk);

export const getCommonJournalDisks = (
  nodeId: NodeID,
  disks: Disk[],
  getInventoryDisk: GetInventoryDisk
): ComputeClusterDisksByMedium[] => getDisksByMedium(
  nodeId,
  disks,
  getInventoryDisk,
  isCommonJournalDisk
);

export const formatDisksByMedium = (disks: ComputeClusterDisksByMedium[]): string => disks
  .map(({ medium, count }) => i18n.tc(
    '{count} {medium} disk|{count} {medium} disks',
    count,
    {
      medium: STORAGE_DISK_TYPE_TEXT[medium] || i18n.t('figure dash'),
      count
    }
  ))
  .join(', ');

export type ComputeClusterNodeTier = {
  tierNum: number;
  locality: Storage_Locality;
  disks: ComputeClusterDisksByMedium[];
  // Journals dedicated to this tier; the node-wide common journals are listed
  // in a row of their own instead.
  journalCount: number;
};

// mds-journal carries a journal for its tier too, on top of the metadata.
// A journal count belongs to a tier number, so the node's journals are counted
// once up front instead of rescanning the disks for every tier row.
const countJournalsByTier = (disks: Disk[]): Record<number, number> => disks
  .reduce<Record<number, number>>((acc, disk) => {
    if (disk.role !== 'journal' && disk.role !== 'mds-journal') {
      return acc;
    }
    if (disk.tier_num === null) {
      return acc;
    }
    return {
      ...acc,
      [disk.tier_num]: (acc[disk.tier_num] || 0) + 1
    };
  }, {});

// The node card lists every tier of the node separately, so unlike the merged
// cluster view these keep the tier number they were assigned in the intent.
export const getNodeStorageTiers = (
  nodeId: NodeID,
  disks: Disk[],
  getInventoryDisk: GetInventoryDisk
): ComputeClusterNodeTier[] => {
  const journalsByTier = countJournalsByTier(disks);
  const tiers = new Map(disks
    .filter((disk): disk is StorageProvisioningStorageDiskIntent => disk.role === 'storage')
    .map(disk => [`${disk.tier_num}:${disk.locality}`, { tierNum: disk.tier_num, locality: disk.locality }]));
  return [...tiers.values()]
    .map(({ tierNum, locality }) => ({
      tierNum,
      locality,
      disks: getDisksByMedium(
        nodeId,
        disks,
        getInventoryDisk,
        disk => disk.role === 'storage' && disk.tier_num === tierNum && disk.locality === locality
      ).sort((a, b) => MEDIUM_ORDER.indexOf(a.medium) - MEDIUM_ORDER.indexOf(b.medium)),
      journalCount: journalsByTier[tierNum] || 0
    }))
    .filter(tier => tier.disks.length)
    .sort((a, b) => a.tierNum - b.tierNum);
};

export const formatNodeStorageTierLabel = (
  { tierNum }: ComputeClusterNodeTier
): string => i18n.t('Tier {tierNum}', { tierNum });

export const formatNodeStorageTier = (tier: ComputeClusterNodeTier): string => {
  const disks = i18n.t('{0} ({1})', [
    formatDisksByMedium(tier.disks),
    STORAGE_LOCALITY_TEXT[tier.locality] || i18n.t('figure dash')
  ]);
  if (!tier.journalCount) {
    return disks;
  }
  return `${disks} ${i18n.tc(
    '+ {count} journal|+ {count} journals',
    tier.journalCount,
    { count: tier.journalCount }
  )}`;
};

// The selected nodes the intent gives no disk in the storage role, or leaves out altogether.
// The intent is the form copy the disk editor writes, not the plan the store holds.
export const nodesWithoutStorageDisks = (
  nodeIds: NodeID[],
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined
): NodeID[] => {
  if (!storageNodes) {
    return [];
  }
  const nodesById = new Map(storageNodes.map(node => [node.node_id, node]));
  return nodeIds.filter(nodeId => !nodesById.get(nodeId)?.disks.some(disk => disk.role === 'storage'));
};

// The intent's storage disks in the tiers a policy's tier selector picks, whatever their medium.
const getPolicyStorageDisks = (
  policy: StoragePolicyIntent,
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined
): { nodeId: NodeID; disk: StorageProvisioningStorageDiskIntent }[] => {
  const tierSelector = policy.tier_selector;
  const intentNodes = storageNodes || [];
  if (!tierSelector) {
    return [];
  }
  const nodes = tierSelector.locality === Storage_Locality.LOCAL
    ? intentNodes.filter(node => tierSelector.node_ids.includes(node.node_id))
    : intentNodes;
  return nodes.flatMap(node => node.disks
    .filter((disk): disk is StorageProvisioningStorageDiskIntent => disk.role === 'storage')
    .filter(disk => disk.locality === tierSelector.locality
      && tierSelector.tier_numbers.includes(disk.tier_num))
    .map(disk => ({ nodeId: node.node_id, disk })));
};

// The distinct tier numbers of the intent's storage disks that a policy's tier selector covers.
export const getPolicyTierNumbers = (
  policy: StoragePolicyIntent,
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined
): number[] => [...new Set(getPolicyStorageDisks(policy, storageNodes).map(({ disk }) => disk.tier_num))];

// The disk types of the tiers a policy's tier selector covers.
export const getPolicyMedia = (
  policy: StoragePolicyIntent,
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined,
  getInventoryDisk: GetInventoryDisk
): DiskType[] => sortMedia(getPolicyStorageDisks(policy, storageNodes)
  .flatMap(({ nodeId, disk }) => {
    const medium = getInventoryDisk(nodeId, disk.id)?.medium;
    return medium ? [medium] : [];
  }));

// Whether the intent has at least one shared tier backed by a shared storage policy.
// The tiers come from the form copy the disk editor writes, so a tier the user turns
// shared counts here; the policies are passed in by the step that owns them.
export const hasSharedTierWithPolicy = (
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined,
  storagePolicies: ComputeProvisioningStoragePolicies | null | undefined
): boolean => {
  if (!storageNodes || !storagePolicies) {
    return false;
  }
  return storagePolicies.items
    .filter(policy => policy.tier_selector?.locality === Storage_Locality.SHARED)
    .some(policy => getPolicyStorageDisks(policy, storageNodes).length > 0);
};

export type ComputeClusterSharedTierOption = {
  tierNum: number;
  media: DiskType[];
  diskCount: number;
  nodeIds: NodeID[];
};

export type ComputeClusterLocalTierOption = {
  tierNum: number;
  nodeId: NodeID;
  media: DiskType[];
  diskCount: number;
};

const storageDisksOf = (
  node: StorageProvisioningNodeIntent,
  locality: Storage_Locality,
  getInventoryDisk: GetInventoryDisk
): { disk: StorageProvisioningStorageDiskIntent; medium: DiskType }[] => node.disks
  .filter((disk): disk is StorageProvisioningStorageDiskIntent => disk.role === 'storage')
  .filter(disk => disk.locality === locality)
  .flatMap((disk) => {
    const medium = getInventoryDisk(node.node_id, disk.id)?.medium;
    return medium ? [{ disk, medium }] : [];
  });

// Every shared tier of the storage intent with the media, disks and nodes backing it.
export const getSharedTierOptions = (
  nodes: StorageProvisioningNodeIntent[] | null | undefined,
  getInventoryDisk: GetInventoryDisk
): ComputeClusterSharedTierOption[] => {
  const tiersByNum = new Map<number, ComputeClusterSharedTierOption>();
  (nodes || []).forEach((node) => {
    storageDisksOf(node, Storage_Locality.SHARED, getInventoryDisk).forEach(({ disk, medium }) => {
      const tier = tiersByNum.get(disk.tier_num) || {
        tierNum: disk.tier_num,
        media: [],
        diskCount: 0,
        nodeIds: []
      };
      tier.media = sortMedia([...tier.media, medium]);
      tier.diskCount += 1;
      if (!tier.nodeIds.includes(node.node_id)) {
        tier.nodeIds.push(node.node_id);
      }
      tiersByNum.set(disk.tier_num, tier);
    });
  });
  return [...tiersByNum.values()];
};

// Every local tier of the storage intent, one per node and tier number, with its media and disk count.
export const getLocalTierOptions = (
  nodes: StorageProvisioningNodeIntent[] | null | undefined,
  getInventoryDisk: GetInventoryDisk
): ComputeClusterLocalTierOption[] => (nodes || []).flatMap((node) => {
  const tiersByNum = new Map<number, ComputeClusterLocalTierOption>();
  storageDisksOf(node, Storage_Locality.LOCAL, getInventoryDisk).forEach(({ disk, medium }) => {
    const tier = tiersByNum.get(disk.tier_num) || {
      tierNum: disk.tier_num,
      nodeId: node.node_id,
      media: [],
      diskCount: 0
    };
    tier.media = sortMedia([...tier.media, medium]);
    tier.diskCount += 1;
    tiersByNum.set(disk.tier_num, tier);
  });
  return [...tiersByNum.values()];
});

// Whether the intent has at least one shared tier, read from the form copy the disk editor writes.
export const hasSharedTier = (
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined,
  getInventoryDisk: GetInventoryDisk
): boolean => getSharedTierOptions(storageNodes, getInventoryDisk).length > 0;

const minOf = (counts: number[]): number => (counts.length ? Math.min(...counts) : 0);

// The failure domains the intent gives a policy's tiers, or null for a policy without tiers.
// A local set counts its smallest tier's disks, 0 for a selected node without one; a shared tier counts hosts or disks by the failure domain.
export const getPolicyFailureDomainCount = (
  policy: StoragePolicyIntent,
  storageNodes: StorageProvisioningNodeIntent[] | null | undefined,
  getInventoryDisk: GetInventoryDisk
): number | null => {
  const tierSelector = policy.tier_selector;
  if (!tierSelector) {
    return null;
  }
  if (tierSelector.locality === Storage_Locality.LOCAL) {
    const localTiers = getLocalTierOptions(storageNodes, getInventoryDisk)
      .filter(tier => tierSelector.tier_numbers.includes(tier.tierNum));
    return minOf(tierSelector.node_ids.flatMap((nodeId) => {
      const counts = localTiers
        .filter(tier => tier.nodeId === nodeId)
        .map(tier => tier.diskCount);
      return counts.length ? counts : [0];
    }));
  }
  return minOf(getSharedTierOptions(storageNodes, getInventoryDisk)
    .filter(tier => tierSelector.tier_numbers.includes(tier.tierNum))
    .map(tier => (policy.failure_domain === StoragePolicyFailureDomain.HOST
      ? tier.nodeIds.length
      : tier.diskCount)));
};
